import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  ProgressBar,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import {
  getUnassignedTasks,
  addTask,
  assignTask,
  getAssignedTasks,
  getTask,
  updateTaskProgress,
  addTaskComment,
  confirmTask,
  getUsers,
} from "../../service/apiService";

function AdminTasks({ user }) {
  const [activeTab, setActiveTab] = useState("unassigned"); // unassigned, assigned, completed, confirmed
  const [unassigned, setUnassigned] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(false);

  // modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", description: "", priority: "medium" });

  const [assignModal, setAssignModal] = useState(false);
  const [assignTaskObj, setAssignTaskObj] = useState(null);
  const [assignStaffId, setAssignStaffId] = useState("");
  const [assignDeadline, setAssignDeadline] = useState("");

  // detail view
  const [detailTaskId, setDetailTaskId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [progressUpdating, setProgressUpdating] = useState(false);
  const [staffList, setStaffList] = useState([]);

  const loadUnassigned = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getUnassignedTasks();
      if (res.status === "success") setUnassigned(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  const loadAssigned = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAssignedTasks();
      if (res.status === "success") setAssigned(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => {
  // load both lists initially
  loadUnassigned();
  loadAssigned();

  // load staff list once
  (async () => {
    try {
      const res = await getUsers();
      if (res && (res.status === "success" || Array.isArray(res))) {
        // support both: {status:'success', data: [...] } or direct array
        const users = res.status === "success" ? res.data : res;
        setStaffList(users);
      } else {
        setStaffList([]);
      }
    } catch (err) {
      setStaffList([]);
    }
  })();
}, [loadUnassigned, loadAssigned]);


  // add task
  const handleAddTask = async () => {
    if (!newTask.title.trim()) return alert('Title required');
    const payload = {...newTask, created_by: user.id};
    const res = await addTask(payload);
    if (res.status === 'success') {
      setShowAddModal(false);
      setNewTask({ title: '', description: '', priority: 'medium' });
      loadUnassigned();
    } else alert(res.message || 'Error');
  };

  // open assign modal
  const openAssign = (task) => {
    setAssignTaskObj(task);
    setAssignStaffId('');
    setAssignDeadline('');
    setAssignModal(true);
  };

  const handleAssign = async () => {
    if (!assignStaffId) return alert('Select staff');
    const res = await assignTask({ task_id: assignTaskObj.id, staff_id: assignStaffId, assigned_by: user.id, deadline: assignDeadline || null });
    if (res.status === 'success') {
      setAssignModal(false);
      loadUnassigned();
      loadAssigned();
    } else alert(res.message || 'Error assigning');
  };

  // detail view
  const openDetail = async (taskId) => {
    setDetailTaskId(taskId);
    const res = await getTask(taskId);
    if (res.status === 'success') setDetail(res);
    else alert(res.message || 'Error');
  };

  const handleProgressUpdate = async (newProgress, newStatus) => {
    setProgressUpdating(true);
    const res = await updateTaskProgress({ task_id: detail.task.id, progress: newProgress, status: newStatus });
    if (res.status === 'success') {
      const refreshed = await getTask(detail.task.id);
      if (refreshed.status === 'success') setDetail(refreshed);
      loadAssigned();
    } else alert(res.message || 'Error updating');
    setProgressUpdating(false);
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    const res = await addTaskComment({ task_id: detail.task.id, user_id: user.id, message: commentText });
    if (res.status === 'success') {
      setCommentText('');
      const refreshed = await getTask(detail.task.id);
      if (refreshed.status === 'success') setDetail(refreshed);
    } else alert(res.message || 'Error');
  };

  const handleConfirm = async () => {
    if (!detail?.task?.id) return;
    const res = await confirmTask({ task_id: detail.task.id, confirmed_by: user.id });
    if (res.status === 'success') {
      // refresh lists
      loadAssigned();
      loadUnassigned();
      setActiveTab('confirmed');
    } else alert(res.message || 'Error confirming');
  };

  // small helper UI
  const formatDate = (d) => d ? new Date(d).toLocaleString() : '-';

  return (
    <div className="p-3">
      <Card className="p-3 mb-3">
        <div className="d-flex gap-2">
          <Button variant={activeTab==='unassigned'?'primary':'light'} onClick={()=>setActiveTab('unassigned')}>Unassigned</Button>
          <Button variant={activeTab==='assigned'?'primary':'light'} onClick={()=>setActiveTab('assigned')}>Assigned / In-Progress</Button>
          <Button variant={activeTab==='completed'?'primary':'light'} onClick={()=>setActiveTab('completed')}>Completed</Button>
          <Button variant={activeTab==='confirmed'?'primary':'light'} onClick={()=>setActiveTab('confirmed')}>Confirmed</Button>
          <div className="ms-auto">
            <Button onClick={()=>setShowAddModal(true)}>+ Add Task</Button>
          </div>
        </div>
      </Card>

      {/* Unassigned tasks */}
      {activeTab==='unassigned' && (
        <Card className="p-3">
          <h5>Unassigned Tasks</h5>
          {loading ? <Spinner/> : (
            <Table striped bordered hover responsive>
              <thead><tr><th>#</th><th>Title</th><th>Priority</th><th>Created</th><th>Action</th></tr></thead>
              <tbody>
                {unassigned.map((t,i)=>(
                  <tr key={t.id}>
                    <td>{i+1}</td>
                    <td><a href="#!" onClick={()=>openDetail(t.id)}>{t.title}</a></td>
                    <td><Badge bg={t.priority==='high'?'danger': t.priority==='medium'?'warning':'secondary'}>{t.priority}</Badge></td>
                    <td>{formatDate(t.created_at)}</td>
                    <td><Button size="sm" onClick={()=>openAssign(t)}>Assign</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {/* Assigned / In-progress */}
      {activeTab==='assigned' && (
        <Card className="p-3">
          <h5>Assigned / In-Progress Tasks</h5>
          {loading ? <Spinner/> : (
            <Table striped bordered hover responsive>
              <thead><tr><th>#</th><th>Title</th><th>Assigned To</th><th>Deadline</th><th>Progress</th><th>Action</th></tr></thead>
              <tbody>
                {assigned.map((t,i)=>(
                  <tr key={t.id}>
                    <td>{i+1}</td>
                    <td><a href="#!" onClick={()=>openDetail(t.id)}>{t.title}</a></td>
                    <td>{t.assigned_to_name||'-'}</td>
                    <td>{formatDate(t.deadline)}</td>
                    <td style={{minWidth:200}}>
                      <ProgressBar now={t.progress||0} label={`${t.progress||0}%`} />
                    </td>
                    <td>
                      <Button size="sm" onClick={()=>openDetail(t.id)}>View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {/* Completed - tasks with status completed but not confirmed */}
      {activeTab==='completed' && (
        <Card className="p-3">
          <h5>Completed Tasks (awaiting confirmation)</h5>
          <Table striped bordered hover responsive>
            <thead><tr><th>#</th><th>Title</th><th>Assigned To</th><th>Completed At</th><th>Action</th></tr></thead>
            <tbody>
              {assigned.filter(t=>t.status==='completed').map((t,i)=>(
                <tr key={t.id}>
                  <td>{i+1}</td>
                  <td><a href="#!" onClick={()=>openDetail(t.id)}>{t.title}</a></td>
                  <td>{t.assigned_to_name}</td>
                  <td>{formatDate(t.completed_at)}</td>
                  <td><Button size="sm" onClick={()=>openDetail(t.id)}>Review</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Confirmed */}
      {activeTab==='confirmed' && (
        <Card className="p-3">
          <h5>Confirmed Tasks</h5>
          <Table striped bordered hover responsive>
            <thead><tr><th>#</th><th>Title</th><th>Assigned To</th><th>Confirmed By</th><th>Confirmed At</th></tr></thead>
            <tbody>
              {assigned.filter(t=>t.status==='confirmed').map((t,i)=>(
                <tr key={t.id}>
                  <td>{i+1}</td>
                  <td>{t.title}</td>
                  <td>{t.assigned_to_name}</td>
                  <td>{t.confirmed_by||'-'}</td>
                  <td>{formatDate(t.confirmed_at)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Task Detail Drawer/Modal */}
      <Modal show={!!detailTaskId} onHide={()=>{setDetailTaskId(null);setDetail(null);}} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Task Detail</Modal.Title></Modal.Header>
        <Modal.Body>
          {!detail ? <Spinner/> : (
            <>
              <h5>{detail.task.title}</h5>
              <div className="text-muted mb-2">Priority: <Badge bg={detail.task.priority}>{detail.task.priority}</Badge></div>
              <p>{detail.task.description}</p>

              <Row className="mb-3">
                <Col md={4}>Assigned To: {detail.task.assigned_to_name||'—'}</Col>
                <Col md={4}>Deadline: {detail.task.deadline ? new Date(detail.task.deadline).toLocaleString() : '-'}</Col>
                <Col md={4}>Status: <strong>{detail.task.status}</strong></Col>
              </Row>

              <div className="mb-3">
                <strong>Progress</strong>
                <div className="d-flex gap-2 align-items-center">
                  <ProgressBar now={detail.task.progress||0} style={{flex:1}} label={`${detail.task.progress||0}%`} />
                  <InputGroup style={{width:150}}>
                    <Form.Control type="number" min={0} max={100} value={detail.task.progress||0} onChange={(e)=> {
                      const v = Math.max(0, Math.min(100, Number(e.target.value)));
                      setDetail(prev=>({...prev, task:{...prev.task, progress:v}}));
                    }} />
                    <Button onClick={()=>handleProgressUpdate(detail.task.progress, detail.task.status)} disabled={progressUpdating}>Update</Button>
                  </InputGroup>
                </div>
              </div>

              <hr />
              <h6>Discussion</h6>
              <div style={{maxHeight:200, overflow:'auto'}} className="mb-2">
                {detail.comments.length===0 && <div className="text-muted">No comments yet.</div>}
                {detail.comments.map(c=>(
                  <div key={c.id} className="mb-2">
                    <strong>{c.name}</strong> <small className="text-muted">{new Date(c.created_at).toLocaleString()}</small>
                    <div>{c.message}</div>
                  </div>
                ))}
              </div>
              <InputGroup className="mb-2">
                <Form.Control value={commentText} onChange={(e)=>setCommentText(e.target.value)} placeholder="Add comment..." />
                <Button onClick={handleAddComment}>Send</Button>
              </InputGroup>

              {detail.task.status==='completed' && (
                <>
                  <hr/>
                  <div className="d-flex justify-content-end">
                    <Button variant="success" onClick={handleConfirm}>Confirm & Close</Button>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>{setDetailTaskId(null);setDetail(null);}}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Add Task Modal */}
      <Modal show={showAddModal} onHide={()=>setShowAddModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Add New Task</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3"><Form.Label>Title</Form.Label><Form.Control value={newTask.title} onChange={(e)=>setNewTask({...newTask,title:e.target.value})} /></Form.Group>
          <Form.Group className="mb-3"><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={4} value={newTask.description} onChange={(e)=>setNewTask({...newTask,description:e.target.value})} /></Form.Group>
          <Form.Group className="mb-3"><Form.Label>Priority</Form.Label><Form.Select value={newTask.priority} onChange={(e)=>setNewTask({...newTask,priority:e.target.value})}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></Form.Select></Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setShowAddModal(false)}>Cancel</Button>
          <Button onClick={handleAddTask}>Create</Button>
        </Modal.Footer>
      </Modal>

      {/* Assign Modal */}
      <Modal show={assignModal} onHide={()=>setAssignModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Assign Task</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-2"><strong>{assignTaskObj?.title}</strong></div>
          <Form.Group className="mb-3">
            <Form.Label>Assign to Staff</Form.Label>
            <Form.Select value={assignStaffId} onChange={(e)=>setAssignStaffId(e.target.value)}>
              <option value="">-- choose --</option>
              {staffList.length && staffList.map(s=> <option key={s.id} value={s.id}>{s.name} ({s.position||s.role})</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Deadline</Form.Label>
            <Form.Control type="datetime-local" value={assignDeadline} onChange={(e)=>setAssignDeadline(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setAssignModal(false)}>Cancel</Button>
          <Button onClick={handleAssign}>Assign</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminTasks;
